/**
 * Phase 8 · C-49 · Shared viz — AvatarStack.
 *
 * Overlapping circles with "+N" overflow chip. Each avatar has a hover card
 * (a plain CSS-positioned `group-hover:` tooltip) showing `name`, `role`, and
 * optional `email`. Pure HTML/Tailwind — no portal, no deps.
 */
import './tokens.css';

export interface AvatarStackPerson {
  name: string;
  avatar?: string | null;
  role: string;
  email?: string | null;
}

export interface AvatarStackProps {
  people: AvatarStackPerson[];
  max?: number;
  /** Fixed avatar diameter in px (default 28). */
  size?: number;
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

function stableColor(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360} 55% 45%)`;
}

export function AvatarStack({ people, max = 4, size = 28 }: AvatarStackProps) {
  const shown = people.slice(0, max);
  const overflow = Math.max(0, people.length - shown.length);

  return (
    <div className="flex items-center" role="list" aria-label={`${people.length} people`}>
      {shown.map((p, i) => (
        <div
          key={`${p.name}-${i}`}
          role="listitem"
          className="group relative"
          style={{ marginLeft: i === 0 ? 0 : -Math.round(size * 0.3) }}
        >
          {p.avatar ? (
            <img
              src={p.avatar}
              alt={p.name}
              className="rounded-full border-2 object-cover"
              style={{ width: size, height: size, borderColor: 'var(--viz-surface)' }}
            />
          ) : (
            <div
              className="flex items-center justify-center rounded-full border-2 text-[10px] font-semibold text-white"
              style={{
                width: size,
                height: size,
                borderColor: 'var(--viz-surface)',
                background: stableColor(p.name),
              }}
              aria-label={p.name}
            >
              {initials(p.name)}
            </div>
          )}

          <div
            className="pointer-events-none invisible absolute left-1/2 top-full z-20 mt-1.5 w-52 -translate-x-1/2 rounded-lg border p-2 opacity-0 shadow-lg transition-opacity duration-100 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
            style={{ background: 'var(--viz-surface)', borderColor: 'var(--viz-border)' }}
            role="tooltip"
          >
            <p className="text-[12px] font-semibold truncate" style={{ color: 'var(--viz-text)' }}>{p.name}</p>
            <p className="text-[11px] capitalize truncate" style={{ color: 'var(--viz-text-muted)' }}>{p.role}</p>
            {p.email && (
              <p className="text-[11px] truncate" style={{ color: 'var(--viz-text-muted)' }}>{p.email}</p>
            )}
          </div>
        </div>
      ))}

      {overflow > 0 && (
        <div
          className="flex items-center justify-center rounded-full border-2 text-[10px] font-semibold"
          style={{
            width: size,
            height: size,
            marginLeft: -Math.round(size * 0.3),
            borderColor: 'var(--viz-surface)',
            background: 'var(--viz-track)',
            color: 'var(--viz-text-muted)',
          }}
          aria-label={`${overflow} more`}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
}
